import { action, observable, makeObservable } from 'mobx';
import { ProductEditForm } from 'modules/product/forms';
import { BaseViewStore } from 'core/stores';
import { appUserLocalStorageKey, notificationError, isSome, translate, isSuperAdminOrganization } from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import { ChannelStore } from 'modules/channel/stores';
import { ApplicationStore } from 'platform/application/stores';
import { channelType, lookupRouteName, sourceOfChange } from 'common/constants';
import { FormHelperService } from 'common/data';
import { errorCode, conversionRates } from 'common/constants';
import { ConfirmModalParams, ModalParams } from 'core/models';
import { BaasicDropdownViewStore } from 'common/stores';
import { GeneralStep, DimensionsStep, VirtualSKUStep } from 'modules/product/wizard/pages';
import { localStorageHandler } from 'core/services';
import { ProductStore } from 'modules/product/stores';
import { CarrierStore } from 'modules/carrier/stores';
import _ from 'lodash';

class ProductEditViewStore extends BaseViewStore {
	@observable automaticBox = true;
	@observable disableUpdate = false;
	@observable currentInventories = [];
	@observable dimensionUnitOptions = [];
	@observable weightUnitOptions = [];
	@observable boxOptions = [];
	@observable tempBoxOptions = [];
	@observable isBoxDropdownLoading = false;
	@observable infiniteSearchPhrase = null;
	@observable pageBox = 1;
	@observable isUpdateMethod = false;
	@observable isMultiPackage = false;
	@observable sellerOptions = [];
	@observable config = [
		{
			name: 'generalStep',
			title: 'PRODUCT.WIZARD.GENERALSTEP',
			component: GeneralStep,
			isValid: true,
			order: 1,
		},
		{
			name: 'dimensionsStep',
			title: 'PRODUCT.WIZARD.DIMENSIONSSTEP',
			component: DimensionsStep,
			isValid: true,
			order: 2,
		},
		{
			name: 'virtualSKUStep',
			title: 'PRODUCT.WIZARD.VIRTUALSKUSTEP',
			component: VirtualSKUStep,
			isValid: true,
			order: 3,
		},
	];
	@observable channelOptions = [];
	isActive = true;
	debouncedBoxSearch = null;
	initialBoxOptions = [];
	newVirtualSkus = [];
	fetchedWarehouses = [];
	product = {};
	searchId = 0;
	productId = this.rootStore.routerStore.routerState.params.id;
	productIdForProductPart = this.rootStore.routerStore.routerState.params.productIdForPart;
	displaySellerDropdown = false;
	isUsualProductCreation = isSome(this.rootStore.routerStore.routerState.params.isUsualProductCreation)
		? this.rootStore.routerStore.routerState.params.isUsualProductCreation
		: true;
	parentSellerId = isSome(this.rootStore.routerStore.routerState.params.sellerId)
		? this.rootStore.routerStore.routerState.params.sellerId
		: null;

	localStorageKey = localStorageHandler.get(appUserLocalStorageKey)
		? localStorageHandler.get(appUserLocalStorageKey).id + '-product-key'
		: null;

	form = new ProductEditForm({
		onSuccess: async () => {
			this.onSubmit();
		},
		onError: () => {
			var errorMsg = 'PRODUCTEDIT.CREATE';
			if (this.productId) {
				errorMsg = 'PRODUCTEDIT.UPDATE';
			}
			this.checkFormValidity();
			this.rootStore.notificationStore.error('PRODUCT.WIZARD.INVALIDFORM');
		},
	});

	applyToAllActiveOrdersConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.createOrUpdateProduct(true);
		},
		onCancel: async () => {
			await this.createOrUpdateProduct(false);
		},
	});

	newProductEntryConfirm = new ConfirmModalParams({
		onCancel: async () => {
			this.rootStore.routerStore.goTo('master.app.main.product.list', {});
		},
	});

	conflictWarningModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.createOrUpdateProduct();
		},
	});

	createBoxModal = new ModalParams({
		onClose: this.closeCreateBoxModal,
		onConfirm: () => {},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		const { orderLineId, sku, channel, orderDetailId } = rootStore.routerStore.routerState.params;
		this.orderLineId = orderLineId ? orderLineId : null;
		this.sku = sku ? sku : null;
		this.channel = channel ? channel : null;
		this.orderDetailId = orderDetailId ? orderDetailId : null;
		this.listStore = new ProductStore(rootStore);
		this.channelStore = new ChannelStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.formHelperService = new FormHelperService();
		if (isSuperAdminOrganization(rootStore)) {
			this.displaySellerDropdown = true;
			this.fetchSellers();
		}
		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		this.form.reset();
		if (this.productId) {
			this.isUpdateMethod = true;
			await this.getProduct(this.productId, {
				embed: [
					'dimensionUnit',
					'weightUnit',
					'productStatus',
					'condition',
					'productCategory',
					'productVirtualSkus',
					'productVirtualSkus.channel',
					'productVirtualSkus.channel.channelType',
					'productVirtualSkus.productVirtualSkuCarriers',
					'productVirtualSkus.productVirtualSkuCarriers.carrier',
					'box',
					'seller',
					'productUpcs',
				],
			});
		} else {
			this.form.clear();
			this.form.$('generalStep.onePerBox').set(true);
			this.form.$('generalStep.isMultiPackage').set(false);
			this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|min:0.01');
			this.form.$('dimensionsStep.ounces').set('rules', 'numeric|integer');
			this.form.$('generalStep.productCategory').set(null);
			this.product.isActive = true;
			if (this.sku && this.channel) {
				this.displaySellerDropdown = false;
				this.addVirtualSku();
			}
			this.addUpcField();
		}
		await this.fetchChannels(this.product == null ? this.parentSellerId : this.product.sellerId);
		this.debouncedBoxSearch = _.debounce((searchPhrase, { action }) => {
			this.searchBoxAsync(searchPhrase, { action });
		}, 500);
		setTimeout(value => {
			this.onPoundsChange(value);
		}, 500);
		await this.getOptions(['condition', 'productStatus', 'productCategory', 'weightUnit', 'dimensionUnit', 'box']);
		if (this.displaySellerDropdown && !this.productId && this.isUsualProductCreation) {
			this.channelOptions = [];
		}
		await this.applicationStore.find({ page: 1, rpp: 50, warehouseOnly: true });
		this.fetchedWarehouses = this.applicationStore.response.item.slice();
		this.totalWarehousesNmbr = this.applicationStore.response.totalRecords;
		this.setDefaults();
		if (!this.isUsualProductCreation) {
			this.form
				.$('generalStep.productPartQuantity')
				.set('rules', 'required|numeric|numericMinValue|notZero|notDecimal');
		}
		if (this.displaySellerDropdown && !this.isUpdateMethod && this.isUsualProductCreation) {
			this.form.$('generalStep.seller').set('rules', 'required');
		}
		this.resume();
	}

	@action.bound
	addUpcField = () => {
		this.form
			.$('generalStep.productUpcs')
			.add([{ id: '', upc: '', productId: this.productId ? this.productId : null }]);
	};

	@action.bound
	async getOptions(keys) {
		_.forEach(keys, async key => {
			await this.lookupStore.find({ page: 1, rpp: 15 }, lookupRouteName[key]);
			this[`${key}Options`] = _.map(this.lookupStore.data, item => {
				if (this.isUpdateMethod == false) {
					if (item.abrv == 'inches') {
						this.form.$(`dimensionsStep.${key}`).value = { label: item.name, value: item.id, extra: item };
					}
					if (item.abrv == 'pounds%20and%20ounces') {
						this.form.$(`dimensionsStep.${key}`).value = { label: item.name, value: item.id, extra: item };
					}
				}
				if (key === 'box') {
					this.initialBoxOptions.push({ label: item.name, value: item.id, extra: item });
					this.tempBoxOptions = [...this.initialBoxOptions];
				}
				return { label: item.name, value: item.id, extra: item };
			});
		});
	}

	@action.bound
	async handleOnBoxMenuScroll() {
		if (this.boxOptions.length < this.pageBox * 15) return;
		this.isBoxDropdownLoading = true;
		this.pageBox++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageBox, rpp: 15 },
			lookupRouteName['box']
		);
		this.tempBoxOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempBoxOptions.length > 0) {
			this.boxOptions = this.boxOptions.concat(this.tempBoxOptions);
		}
		this.isBoxDropdownLoading = false;
	}

	@action.bound
	async onMenuClose() {
		this.pageBox = 1;
		this.infiniteSearchPhrase = null;
		await this.getOptions(['box']);
	}

	@action.bound
	async searchBoxAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.page = 1;
		}
		if (action === 'input-change') {
			this.isBoxDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageBox = 1;
			this.boxOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
			};
			await this.lookupStore.find(filter, lookupRouteName['box']);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.boxOptions = newOptions;
			this.isBoxDropdownLoading = false;
		}
	}

	@action.bound
	async setDefaults() {
		if (this.form.$('generalStep.condition').value === '') {
			await this.lookupStore.find({ search: 'new' }, lookupRouteName.condition);
			var exactMatch = null;
			if (this.lookupStore.response.item.length > 1) {
				exactMatch = _.find(this.lookupStore.response.item, lookup => {
					return lookup.abrv === 'new';
				});
			} else {
				exactMatch = this.lookupStore.response.item[0];
			}
			this.form.$('generalStep.condition').value = { value: exactMatch.id, label: exactMatch.name };
		}
		if (this.form.$('generalStep.productStatus').value === '') {
			await this.lookupStore.find({ search: 'available' }, lookupRouteName.productStatus);
			var exactMatch = null;
			if (this.lookupStore.response.item.length > 1) {
				exactMatch = _.find(this.lookupStore.response.item, lookup => {
					return lookup.abrv === 'available';
				});
			} else {
				exactMatch = this.lookupStore.response.item[0];
			}
			this.form.$('generalStep.productStatus').value = { value: exactMatch.id, label: exactMatch.name };
		}
	}

	// Validate all steps.
	@action.bound
	async checkFormValidity() {
		_.each(this.config, step => {
			if (this.form.has(`${step.name}`)) {
				this.form
					.$(`${step.name}`)
					.validate()
					.then(({ isValid }) => {
						if (!isValid) {
							step.isValid = isValid;
						} else {
							step.isValid = isValid;
						}
					});
			}
		});
		this.form.invalidate();
		this.form.showErrors();
	}

	// Validate individual step.
	@action.bound
	async checkValidityForStep(stepNumber) {
		_.each(this.config, step => {
			if (step.order == stepNumber) {
				if (this.form.has(`${step.name}`)) {
					this.form
						.$(`${step.name}`)
						.validate()
						.then(({ isValid }) => {
							if (!isValid) {
								step.isValid = isValid;
							} else {
								step.isValid = isValid;
							}
						});
				}
			}
			if (!step.isValid) {
				this.form.invalidate();
				this.form.showErrors();
			}
		});
	}

	@action.bound
	async checkMultiProduct(value) {
		this.isMultiPackage = value;
		this.onWeightUnitChange(this.form.$('dimensionsStep.weightUnit').value);
		if (value) {
			this.form.$('dimensionsStep.length').set('rules', 'numeric|notZero');
			this.form.$('dimensionsStep.width').set('rules', 'numeric|notZero');
			this.form.$('dimensionsStep.height').set('rules', 'numeric|notZero');
			this.checkValidityForStep(2);
		} else {
			this.form.$('dimensionsStep.length').set('rules', 'required|numeric|notZero');
			this.form.$('dimensionsStep.width').set('rules', 'required|numeric|notZero');
			this.form.$('dimensionsStep.height').set('rules', 'required|numeric|notZero');
		}
	}

	@action.bound
	addVirtualSku = () => {
		this.searchId += 1;
		const field = this.form
			.$('virtualSKUStep')
			.$('productVirtualSkus')
			.add({
				fields: {
					sku: this.sku ? this.sku : '',
					channel:
						this.channel &&
						!this.form
							.$('virtualSKUStep.productVirtualSkus')
							.value.some(virtualSku => virtualSku.channel.value === this.channel.id)
							? { value: { value: this.channel.id, label: this.channel.name, extra: this.channel } }
							: null,
					description: '',
					id: '',
					searchId: this.searchId,
					postInventory: this.isActive,
					isActive: this.isActive,
					carriers: null,
					isNonPrime: false,
				},
			});

		field.carrierDropdownStore = new BaasicDropdownViewStore({
			field: field.$('carriers'),
			isMulti: true,
			store: new CarrierStore(this.rootStore),
			filterProperties: {
				channelIds: field.$('channel').value ? [field.$('channel').value.value] : [],
			},
		});
		this.newVirtualSkus.push(field);
	};

	@action
	async onSubmit() {
		this.getValuesFromProductForm(this.form);
		if (this.newVirtualSkus.length > 0) {
			this.openApplyToAllActiveOrdersConfirmModal();
		} else {
			this.createOrUpdateProduct(false);
		}
	}

	onSearch = async (input, key) => {
		const filter = {
			search: input || null,
			pageNumber: 1,
			pageSize: 50,
			orderDirection: 'asc',
			searchFields: 'name',
		};
		await this.lookupStore.find(filter, lookupRouteName[key]);
		const newOptions = [];
		if (this.lookupStore.data.length > 0) {
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
		}
		return newOptions;
	};

	@action.bound
	getValuesFromProductForm(form) {
		var product = new Object();
		_.each(form.values(), function(step) {
			_.merge(product, step);
		});
		this.product = Object.assign(this.product, product);
	}

	@action.bound
	async getProduct(id, options) {
		this.product = await this.listStore.getProduct(id, options);
		this.isActive = this.product.isActive;
		this.updateForm(this.product);
	}

	@action.bound
	setVirtualSkuActivity = (pvsValue, postInventory) => {
		if (pvsValue != null && postInventory != null) {
			postInventory.value = pvsValue;
		}
	};

	@action.bound
	updateForm(product) {
		if (product.onePerBox) {
			this.form.$('generalStep.onePerBox').value = product.onePerBox;
		}
		if (product.box != null) {
			this.form.$('dimensionsStep.box').value = { label: product.box.name, value: product.box.id, extra: product.box };
		}
		this.formHelperService.updateForm(this.form, product, true);
		this.form.$('generalStep.isMultiPackage').set(product.isMultiProduct);
		if (product.productVirtualSkus) {
			_.each(product.productVirtualSkus, (virtualSku, idx) => {
				const field = this.form.$(`virtualSKUStep.productVirtualSkus[${idx}]`);
				field.$('channel').set('value', {
					value: virtualSku.channel.id,
					label: virtualSku.channel.name,
					extra: virtualSku.channel,
				});
				field.$('channel').set('rules', 'required');
				if (virtualSku.productVirtualSkuCarriers.length > 0) {
					const fieldValues = _.map(virtualSku.productVirtualSkuCarriers, productVirtualSkuCarrier => {
						return {
							value: productVirtualSkuCarrier.carrierId,
							label: productVirtualSkuCarrier.carrier.name,
							id: productVirtualSkuCarrier.id,
						};
					});
					field.$('carriers').set('value', fieldValues);
				}
				field.carrierDropdownStore = new BaasicDropdownViewStore({
					field: field.$('carriers'),
					isMulti: true,
					filterProperties: {
						channelIds: [field.$('channel').value.value],
					},
					store: new CarrierStore(this.rootStore),
				});
			});
		}

		this.setLookupValues(product);
	}

	@action.bound onAutomaticBoxCheck() {
		this.automaticBox = !this.automaticBox;
	}

	setLookupValues = product => {
		if (product.condition) {
			this.form.$('generalStep.condition').value = {
				value: product.condition.id,
				label: product.condition.name,
				extra: product.condition,
			};
		}
		if (product.productStatus) {
			this.form.$('generalStep.productStatus').value = {
				value: product.productStatus.id,
				label: product.productStatus.name,
				extra: product.productStatus,
			};
		}
		if (product.productCategory) {
			this.form.$('generalStep.productCategory').value = {
				value: product.productCategory.id,
				label: product.productCategory.name,
				extra: product.productCategory,
			};
		}

		this.form.$('generalStep.seller').value = {
			value: product.sellerId,
			label: product.seller.applicationTitle,
		};

		if (product.box) {
			this.form.$('dimensionsStep.box').value = { value: product.box.id, label: product.box.name, extra: product.box };
		}
		if (product.weightUnit.abrv != 'kilograms') {
			if (product.weight != 0 && product.weight != null) {
				this.form.$('dimensionsStep.pounds').value = parseInt(product.weight);
				if (product.weight % 1 != 0) {
					this.form.$('dimensionsStep.ounces').value = parseInt((product.weight % 1) * conversionRates.poundsAndOunces);
				}
			}
			this.form.$('dimensionsStep.weight').set('value', 0);
			if (this.form.$('dimensionsStep.ounces').value == 0) {
				this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|decimalLength|notZero');
			} else {
				this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|decimalLength');
			}
			this.form.$('dimensionsStep.ounces').set('rules', 'numeric|integer');
		} else {
			this.form.$('dimensionsStep.weight').set('rules', 'required|numeric|numericMinValue|decimalLength|notZero');
		}
		this.form.$('generalStep.onePerBox').value = product.onePerBox;
		this.form.$('dimensionsStep.dimensionUnit').value = {
			value: product.dimensionUnit.id,
			label: product.dimensionUnit.name,
			extra: product.dimensionUnit,
		};
		this.form.$('dimensionsStep.weightUnit').value = {
			value: product.weightUnit.id,
			label: product.weightUnit.name,
			extra: product.weightUnit,
		};
		this.checkMultiProduct(product.isMultiProduct);
	};

	@action
	createOrUpdateProduct = async applyToAllActiveOrders => {
		this.suspend();
		try {
			const {
				generalStep: { isMultiPackage },
			} = this.form.values();

			// Trim all trailing and leading whitespaces from all skus.
			_.forEach(this.product.productVirtualSkus, virtualSku => {
				virtualSku.sku = virtualSku.sku.trim();
				if (virtualSku.channel.extra.channelType.name !== channelType.amazon) {
					virtualSku.isNonPrime = false;
				}
				virtualSku.channelId = virtualSku.channel.value;
				virtualSku.channel = null;
				virtualSku.productVirtualSkuCarriers = _.map(virtualSku.carriers, item => {
					return {
						productVirtualSkuId: virtualSku.id,
						carrierId: item.value,
						id: item.id != null ? item.id : '',
					};
				});
			});

			if (this.automaticBox) {
				const boxDimensions = {
					boxHeight: this.product.height,
					boxWidth: this.product.width,
					boxLength: this.product.length,
				};
				const box = {
					name: `${this.product.height} x ${this.product.width} x ${this.product.length}`,
					json: JSON.stringify(boxDimensions),
					organizationId: this.product.seller.value,
				};
				const response = await this.lookupStore.createLookup(
					box,
					lookupRouteName.box,
					sourceOfChange.createOrEditProduct
				);
				// If box already exists
				if (response.message) {
					this.product.box = response.box;
					this.product.box.value = response.box.id;
				} else {
					this.product.box = response;
					this.product.box.value = response.id;
				}
			}

			if (this.product.weightUnit.label == 'Pounds and Ounces') {
				let { ounces, pounds } = this.form.values().dimensionsStep;
				if (!ounces) ounces = 0;
				if (!pounds) pounds = 0;
				const convertedOunces = parseInt(ounces) / conversionRates.poundsAndOunces;
				if (ounces) {
					this.product.weight = parseInt(pounds) + convertedOunces;
				} else {
					this.product.weight = parseInt(pounds);
				}
			}

			let product = {
				id: this.productId,
				conditionid: this.product.condition.value,
				dimensionUnitId: this.product.dimensionUnit.value,
				weightUnitId: this.product.weightUnit.value,
				productCategoryId: isSome(this.product.productCategory) ? this.product.productCategory.value : null,
				productStatusId: this.product.productStatus.value,
				boxId: isSome(this.product.box) ? this.product.box.value : null,
				orderLineId: this.orderLineId != null ? this.orderLineId : null,
				description: this.product.description,
				height: this.product.height,
				length: this.product.length,
				name: this.product.name,
				onePerBox: this.product.onePerBox,
				productVirtualSkus: this.product.productVirtualSkus,
				mainSku: this.product.mainSku,
				productUpcs: this.product.productUpcs,
				weight: this.product.weight,
				width: this.product.width,
				isMultiProduct: isMultiPackage,
				isActive: this.product.isActive,
				sellerId: this.product.seller.value,
				asin: this.product.asin,
				applyToAllActiveOrders,
			};

			if (this.parentSellerId) product.sellerId = this.parentSellerId;

			if (!this.isUsualProductCreation) {
				const { productPartQuantity } = this.form.values().generalStep;
				product.multiPackageProducts = [
					{
						parentProductId: this.productIdForProductPart,
						productId: this.productId,
						quantity: productPartQuantity,
					},
				];
				product.sellerId = this.parentSellerId;
			}
			if (this.productId) {
				await this.listStore.updateProduct(product, sourceOfChange.editProductEditPage);
				this.onFinish(['LOOKUPUPDATE.PRODUCT', { name: this.product.name }]);
			} else {
				await this.listStore.createProduct(product, sourceOfChange.createProductEditPage);
				localStorageHandler.remove(this.localStorageKey);
				if (this.orderLineId != null) {
					this.rootStore.notificationStore.success('CREATEPRODUCTANDLINKORDERLINE');
					this.rootStore.viewStore.countUnlinkedOrderLines();
					this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.detail', {
						id: this.orderDetailId,
					});
				} else {
					this.onFinish('CREATEPRODUCT');
				}
			}
		} catch (err) {
			if (err.statusCode == errorCode.existingResource) {
				this.applyToAllActiveOrdersConfirmModal.close();
				this.resume();
				this.rootStore.notificationStore.error('UNIQUESKUORUPC');
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	onWeightUnitChange = value => {
		const { ounces, pounds, weight } = this.form.values().dimensionsStep;
		let newWeight = 0;
		if (value.label == 'Kilograms') {
			if (pounds != '' && pounds > 0) {
				newWeight += pounds / conversionRates.kilogramsAndPounds;
			}
			if (ounces != '' && ounces > 0) {
				newWeight += ounces * conversionRates.ouncesAndKilograms;
			}
			if (newWeight > 0) {
				this.form.$('dimensionsStep.weight').set('value', newWeight.toFixed(2));
			}
			if (!this.isMultiPackage) {
				this.form.$('dimensionsStep.weight').set('rules', 'required|numeric|numericMinValue|decimalLength|notZero');
			} else {
				this.form.$('dimensionsStep.weight').set('rules', 'numeric|numericMinValue|decimalLength|notZero');
			}
			this.form.$('dimensionsStep.pounds').set('rules', '');
			this.form.$('dimensionsStep.ounces').set('rules', '');
			this.form.$('dimensionsStep.pounds').set('value', '');
			this.form.$('dimensionsStep.ounces').set('value', '');
		} else {
			if (weight != '' && weight > 0) {
				const pounds = parseFloat((weight * conversionRates.kilogramsAndPounds).toFixed(2));
				const poundsDecimal = pounds % 1;
				let ounces = 0;
				if (poundsDecimal) {
					ounces = parseFloat(poundsDecimal * conversionRates.poundsAndOunces);
				}
				this.form.$('dimensionsStep.pounds').set('value', pounds.toFixed(0));
				if (ounces.toFixed(1) != 0) {
					this.form.$('dimensionsStep.ounces').set('value', ounces.toFixed(0));
				}
			}
			this.form.$('dimensionsStep.weight').set('value', '');
			this.form.$('dimensionsStep.weight').set('rules', '');
			if (!this.isMultiPackage) {
				if (this.form.$('dimensionsStep.ounces') == 0) {
					this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|numericMinValue|decimalLength|notZero');
				} else {
					this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|numericMinValue|decimalLength');
				}
				this.form.$('dimensionsStep.ounces').set('rules', 'numeric|integer');
			} else {
				this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|numericMinValue|decimalLength');
				this.form.$('dimensionsStep.ounces').set('rules', 'numeric|integer');
			}
		}
	};

	timer = value => {
		setTimeout(() => {
			this.onPoundsChange(value);
		}, 500);
	};

	onPoundsChange = pound => {
		const poundsDecimal = pound % 1;
		let ounces = 0;
		if (poundsDecimal) {
			ounces = parseFloat(poundsDecimal * conversionRates.poundsAndOunces).toFixed(0);
			this.form.$('dimensionsStep.ounces').set('value', ounces);
			this.form.$('dimensionsStep.pounds').set('value', pound - poundsDecimal);
			if (ounces > 0) {
				this.form.$('dimensionsStep.pounds').set('rules', 'numeric|numericMinValue|decimalLength');
			} else {
				this.form.$('dimensionsStep.pounds').set('rules', 'numeric|numericMinValue|decimalLength|notZero');
			}
		} else {
			if (!this.isMultiPackage) {
				if (this.form.$('dimensionsStep.ounces').get('value') > 0) {
					this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|numericMinValue|decimalLength');
				} else {
					this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|numericMinValue|decimalLength|notZero');
				}
			}
		}
	};

	onOuncesChange = ounce => {
		if (ounce > 0) {
			this.form.$('dimensionsStep.pounds').set('rules', 'numeric|numericMinValue|decimalLength');
			this.form.$('dimensionsStep.pounds').validate();
		} else {
			if (!this.isMultiPackage) {
				this.form.$('dimensionsStep.pounds').set('rules', 'required|numeric|numericMinValue|decimalLength|notZero');
				if (this.form.$('dimensionsStep.pounds').get('value') < 0.01) {
					this.form.$('dimensionsStep.pounds').invalidate(translate('NOTZEROVALUE'));
				}
			}
		}
	};

	@action.bound
	onChange = async (value, field, isWeightUnitChage = false, isChannelChange = false, parentField = {}) => {
		field.set(value);
		if (isWeightUnitChage) {
			this.onWeightUnitChange(value);
		}
		if (isChannelChange) {
			parentField.carrierDropdownStore.clear();
			await parentField.carrierDropdownStore.updateFilter({ channelIds: [value.value] });
		}
	};

	@action.bound
	disableUpdateInventory = field => {
		if (field == null || field.extra == null || field.extra.updateInventory == true) {
			this.disableToggle = false;
			return false;
		}
		this.disableToggle = true;
		return true;
	};

	@action.bound
	filterOptions = searchValue => {
		if (searchValue === '') {
			this.boxOptions = this.initialBoxOptions;
		} else {
			this.boxOptions = _.filter(this.initialBoxOptions, item => {
				return item.label.toLowerCase().includes(searchValue.toLowerCase());
			});
		}
	};

	@action.bound
	onFinish = message => {
		this.rootStore.notificationStore.success(message);
		if (this.isUsualProductCreation) {
			this.rootStore.routerStore.goTo('master.app.main.product.list');
		} else {
			this.rootStore.routerStore.goTo('master.app.main.product.list.detail', { id: this.productIdForProductPart });
		}
	};

	@action.bound onDelete(e, field, virtualSkus) {
		if (this.channel && field.value.channel.value === this.channel.id) return;
		this.form.$(field.path).onDel(e);
		if (virtualSkus.size == 0) {
			this.form.$('virtualSKUStep.productVirtualSkus').value = [];
		}
		const fieldValue = field.value;
		const idxToRemove = _.findIndex(this.newVirtualSkus, item => {
			return item.searchId == fieldValue.searchId;
		});
		if (idxToRemove != -1) {
			this.newVirtualSkus.splice(idxToRemove, 1);
		}
	}

	@action.bound onCheck(check) {
		if (check) {
			this.form.$('generalStep.onePerBox').value = check.currentTarget.checked;
		}
	}

	@action.bound
	async onInputChange(input) {
		if (input === '') {
			this.scrollLoadBox = false;
			this.infiniteEndBox = false;
		}
	}

	@action.bound handleBackButtonClick = () => {
		if (this.isUsualProductCreation) {
			this.rootStore.routerStore.goTo('master.app.main.product.list');
		} else {
			this.rootStore.routerStore.goTo('master.app.main.product.list.detail', { id: this.productIdForProductPart });
		}
	};

	@action.bound async openConflictWarningModal(message) {
		this.conflictWarningModal.open({
			message: message,
		});
	}

	@action.bound async openApplyToAllActiveOrdersConfirmModal() {
		this.applyToAllActiveOrdersConfirmModal.open({
			message: ['PRODUCTVIEW.AUTOMATICALLYLINKSKUS'],
		});
	}

	@action.bound async openCreateBoxModal() {
		this.createBoxModal.open();
	}

	@action.bound closeCreateBoxModal() {
		this.createBoxModal.close();
	}

	@action.bound
	fetchSellers = async () => {
		this.applicationStore = new ApplicationStore(this.rootStore);
		const response = await this.applicationStore.find({
			page: 1,
			rpp: 50,
			sellerOnly: true,
		});
		this.sellerOptions = _.map(response.item, item => {
			return { value: item.id, label: item.applicationTitle };
		});
		let seller = localStorageHandler.get('selectedSeller');
		if (seller) {
			if (this.sellerOptions.some(o => o.value === seller.value)) {
				this.form.$('generalStep.seller').set(seller);
			}
			this.updateChannels(seller);
		}
	};

	@action.bound
	onSellerChange = item => {
		this.form.$('generalStep.seller').set(item);
		this.updateChannels(item);
	};

	getRef = ref => {
		this.boxRef = ref;
	};

	@action.bound updateChannels(item) {
		for (let i = this.form.$(`virtualSKUStep.productVirtualSkus`).fields.data_.size; i > 0; i--) {
			this.form.$(`virtualSKUStep.productVirtualSkus[${i - 1}].channel`).reset();
		}

		if (item == null) {
			this.channelOptions = [];
		} else {
			this.fetchChannels(item.value);
		}
	}

	@action.bound
	fetchChannels = async (sellerId = null) => {
		const json =
			sellerId == null
				? { page: 1, rpp: 150, embed: ['channelType'] }
				: { page: 1, rpp: 150, embed: ['channelType'], sellerIds: [sellerId] };
		await this.channelStore.findChannel(json);
		this.channelOptions = _.map(this.channelStore.data, item => {
			return {
				value: item.id,
				label: item.name,
				extra: item,
			};
		});
	};

	@action.bound
	removeUpc = key => {
		this.form.$('generalStep.productUpcs').del(key);
	};
}

export default ProductEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/product/stores/ProductEditViewStore.js